import { useState } from 'react';
import PropTypes from 'prop-types';
import { Icon, Tooltip2, useOpenClose } from '@evertrue/et-components';
import ProfileCardHeader from '../profile-card-header';
import PhilanthropicIndicatorsCard from './philanthropic-indicators-card';
import CapacityRatingsCard from './capacity-ratings-card';
import GivingCard from './giving-card';
import FinancialSignalsCard from './financial-signals-card';
import PhilanthropicSignalsModal from './philanthropic-signals-modal';
import NetWorthModal from './net-worth-modal';
import {
  formatNetWorths,
  transformDonorSearchData,
  formatCapacityRange,
  formatCurrency,
  selectGivingScore,
} from './utils/index';
import useDonorSearchTable from 'hooks/use-donor-search-table';
import { mostRecentCreate } from './enrichment-utils';

/**
 * Parent container for philanthropic and financial signals data.
 * Contains sub-cards for capacity ratings, giving, financial signals, and philanthropic indicators.
 */
const PhilanthropicFinancialSignals = ({
  wealthIndicators = [],
  philInterests = [],
  contactAttributes,
  onDelete,
  donorSearchData = {},
  /** DB-sourced DonorSearch AI score records (donorsearch_ai_scores from GQL) */
  donorsearchAiScores = [],
  hasEnrichmentAccess = false,
  /** Whether the donorsearch_api feature flag is enabled */
  hasDonorsearchApi = false,
  /** Whether DonorSearch API is fully enabled (donorsearch + donorsearch_api flags + profile_id) */
  canUseDonorsearchApi = false,
  /** Raw net worth data from wealth_attributes (single source of truth) */
  netWorths = [],
  /** DonorSearch profile ID for API calls */
  donorsearchProfileId,
  /** High/low quality count breakdowns from /summary API */
  summaryCounts = null,
  /** Whether the /summary API is still loading */
  summaryLoading = false,
  /** DPS score from et_score.dds_score (fallback when DonorSearch data is unavailable) */
  dpsScore = null,
}) => {
  const [isModalOpen, openModal, closeModal] = useOpenClose();
  const [isNetWorthModalOpen, openNetWorthModal, closeNetWorthModal] = useOpenClose();
  const [modalInitialTab, setModalInitialTab] = useState('charitable');
  const [modalInitialSection, setModalInitialSection] = useState('highQuality');

  /**
   * Opens the drill-down modal with the specified tab and confidence section.
   */
  const handleDrillDown = (category, section = 'highQuality') => {
    setModalInitialTab(category);
    setModalInitialSection(section);
    openModal();
  };

  // Fetch table API data when DonorSearch API is available (shared with modal)
  const tableHookResult = useDonorSearchTable(
    canUseDonorsearchApi ? contactAttributes?.contactId : null,
    donorsearchProfileId,
    'charitable'
  );
  const { summary: tableSummary, loading: tableLoading } = tableHookResult;

  // Transform raw API response to component-ready format
  const {
    dsRating,
    capacityWealth,
    capacityGiving,
    charitableGiving,
    politicalGiving,
    crowdfunding,
    realEstate,
    secHoldings,
    formattedUpdateDate,
  } = transformDonorSearchData(donorSearchData, summaryCounts);

  // Suppress GQL fallback values while API-path data is still loading
  const capacityLoading = canUseDonorsearchApi && tableLoading;
  const countsLoading = canUseDonorsearchApi && summaryLoading;
  const effectiveCapacityWealth = capacityLoading
    ? null
    : tableSummary?.estimatedCapacity != null
      ? formatCapacityRange(tableSummary.estimatedCapacity)
      : capacityWealth;
  const effectiveCapacityGiving = capacityLoading
    ? null
    : tableSummary?.largestGiftLow != null
      ? formatCurrency(tableSummary.largestGiftLow)
      : capacityGiving;

  // TEMPORARY: Use table-derived counts for real estate as source of truth
  // until DonorSearch fixes the /summary quality count endpoint.
  const effectiveRealEstate = (() => {
    if (tableSummary?.realEstateFromTable) return tableSummary.realEstateFromTable;
    if (summaryCounts) return realEstate;
    if (countsLoading || !tableSummary) return realEstate;
    const base = realEstate || { amount: null, highCount: 0, lowCount: 0 };
    const amount = tableSummary.realEstateEst != null ? tableSummary.realEstateEst : base.amount;
    const highCount = base.highCount || tableSummary.realEstateCount || 0;
    return { ...base, amount, highCount };
  })();

  // Score selection now sourced from the DB (donorsearch_ai_scores) rather than the live API,
  // so it no longer depends on the donorsearchApi LD flag. Priority: DSAI > ECR > MLR > DPS.
  const { type: effectiveGivingScoreType, value: effectiveGivingScoreValue } = selectGivingScore(
    donorsearchAiScores,
    dpsScore
  );

  const title = (
    <span className="profile-enrichment-title-wrapper">
      <Tooltip2 trigger={<img src="/images/donor-search.svg" alt="Donor Search Logo" />}>
        Powered by DonorSearch
      </Tooltip2>
      <Tooltip2 trigger={<div className="profile-enrichment-title">Philanthropic and Financial Signals</div>}>
        {formattedUpdateDate
          ? `Data was last updated on ${formattedUpdateDate}`
          : 'Philanthropic and Financial Signals'}
      </Tooltip2>
    </span>
  );

  // Format netWorths using the same utility as WealthIndicators
  const formattedNetWorths = formatNetWorths(netWorths);
  const activeNetWorths = formattedNetWorths.filter(({ rejected }) => !rejected);
  // Get the first active net worth value for display in the card
  const estNetWorthDisplay = activeNetWorths.length > 0 ? activeNetWorths[0].value : null;
  const netWorthLastUpdated = netWorths.length > 0 ? mostRecentCreate(netWorths, 'updated_at', 'MMM DD, YYYY') : null;
  return (
    <div className="profile--enrichment-philanthropic-signals">
      <ProfileCardHeader title={title}>
        {canUseDonorsearchApi && (
          <button
            type="button"
            className="philanthropic-signals--edit-btn"
            onClick={() => handleDrillDown('charitable', 'highQuality')}
            aria-label="Edit philanthropic and financial signals"
          >
            <Icon icon="edit" title="Edit philanthropic and financial signals" />
          </button>
        )}
      </ProfileCardHeader>

      <div className="profile--enrichment-philanthropic-signals-content">
        {!hasEnrichmentAccess ? (
          <div className="profile-enrichment-teaser">
            <p className="teaser-highlight">Could this be your next major donor?</p>
            <p>Powered by DonorSearch (the leader in philanthropic data), see wealth and giving capacity instantly.</p>
            <p>Contact your CSM to activate!</p>
          </div>
        ) : (
          <div className="philanthropic-signals-cards-row">
            <CapacityRatingsCard
              dsRating={dsRating}
              capacityWealth={effectiveCapacityWealth}
              capacityLoading={capacityLoading}
              capacityGiving={effectiveCapacityGiving}
            />
            <GivingCard
              givingScoreType={effectiveGivingScoreType}
              givingScoreValue={effectiveGivingScoreValue}
              charitableGiving={countsLoading ? null : charitableGiving}
              politicalGiving={countsLoading ? null : politicalGiving}
              crowdfunding={countsLoading ? null : crowdfunding}
              loading={countsLoading || capacityLoading}
              hasEnrichmentAccess={hasEnrichmentAccess}
              onDrillDown={canUseDonorsearchApi ? handleDrillDown : undefined}
              showUpsellTooltip={hasEnrichmentAccess && !hasDonorsearchApi}
            />
            <FinancialSignalsCard
              realEstate={countsLoading ? null : effectiveRealEstate}
              secHoldings={countsLoading ? null : secHoldings}
              loading={countsLoading}
              estNetWorth={estNetWorthDisplay}
              hasEnrichmentAccess={hasEnrichmentAccess}
              onDrillDown={canUseDonorsearchApi ? handleDrillDown : undefined}
              onNetWorthClick={netWorths.length > 0 ? openNetWorthModal : undefined}
              showUpsellTooltip={hasEnrichmentAccess && !hasDonorsearchApi}
            />
          </div>
        )}

        <PhilanthropicIndicatorsCard
          wealthIndicators={wealthIndicators}
          philInterests={philInterests}
          contactAttributes={contactAttributes}
          onDelete={onDelete}
        />
      </div>

      <PhilanthropicSignalsModal
        isOpen={isModalOpen}
        closeModal={closeModal}
        contactName={contactAttributes?.contactName}
        tableHookResult={tableHookResult}
        initialTab={modalInitialTab}
        initialSection={modalInitialSection}
      />

      <NetWorthModal
        isOpen={isNetWorthModalOpen}
        closeModal={closeNetWorthModal}
        contactName={contactAttributes?.contactName}
        contactId={contactAttributes?.contactId}
        netWorths={netWorths}
        onDelete={onDelete}
        updateDate={netWorthLastUpdated}
      />
    </div>
  );
};

PhilanthropicFinancialSignals.propTypes = {
  wealthIndicators: PropTypes.array,
  philInterests: PropTypes.array,
  contactAttributes: PropTypes.shape({
    contactId: PropTypes.number,
    contactName: PropTypes.string,
  }),
  /** Callback when delete/restore completes (triggers data refetch) */
  onDelete: PropTypes.func,
  /** Raw DonorSearch API response (snake_case fields) */
  donorSearchData: PropTypes.shape({
    ds_rating: PropTypes.string,
    estimated_capacity: PropTypes.number,
    largest_gift_upper_estimate: PropTypes.number,
    largest_gift_lower_estimate: PropTypes.number,
    annual_fund_likelihood: PropTypes.number,
    major_gift_likelihood: PropTypes.number,
    planned_gift_likelihood: PropTypes.number,
    total_gift_amount: PropTypes.number,
    total_gift_count: PropTypes.number,
    likely_giving_amount: PropTypes.number,
    likely_giving_count: PropTypes.number,
    political_giving_amount: PropTypes.number,
    political_giving_count: PropTypes.number,
    likely_political_giving_amount: PropTypes.number,
    likely_political_giving_count: PropTypes.number,
    real_estate_value: PropTypes.number,
    real_estate_count: PropTypes.number,
    real_estate_in_trust: PropTypes.string,
    number_states_with_real_estate: PropTypes.number,
    sec_stock_value: PropTypes.number,
    sec_insider: PropTypes.string,
    business_revenue: PropTypes.number,
    business_affiliation: PropTypes.string,
    foundation_board_member: PropTypes.string,
    foundation_income: PropTypes.number,
    grank_seeking_board_member: PropTypes.string,
    quality_score: PropTypes.number,
    pgid_rating: PropTypes.number,
    rfm_score: PropTypes.number,
    created_at: PropTypes.string,
    updated_at: PropTypes.string,
    rejected: PropTypes.bool,
  }),
  /** DB-sourced DonorSearch AI score records (donorsearch_ai_scores from GQL) */
  donorsearchAiScores: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
      score_value: PropTypes.number,
      score_label: PropTypes.string,
      created_at: PropTypes.string,
      updated_at: PropTypes.string,
    })
  ),
  hasEnrichmentAccess: PropTypes.bool,
  /** Whether the donorsearch_api feature flag is enabled */
  hasDonorsearchApi: PropTypes.bool,
  /** Whether DonorSearch API is fully enabled (donorsearch + donorsearch_api flags + profile_id) */
  canUseDonorsearchApi: PropTypes.bool,
  /** Raw net worth data from wealth_attributes (single source of truth for Est. Net Worth) */
  netWorths: PropTypes.array,
  /** DonorSearch profile ID for API calls */
  donorsearchProfileId: PropTypes.number,
  /** High/low quality count breakdowns from /summary API */
  summaryCounts: PropTypes.object,
  /** Whether the /summary API is still loading */
  summaryLoading: PropTypes.bool,
  /** DPS score from et_score.dds_score (fallback when DonorSearch data is unavailable) */
  dpsScore: PropTypes.number,
};

export default PhilanthropicFinancialSignals;
